/**
 * Primeiro acesso: troca da senha provisória pela definitiva (design
 * pharu-primeiro-acesso-handoff, out/2026).
 *
 * Só apresentação e validação local; `definirSenha(dados)` (do container
 * PrimeiroAcesso) fala com o backend e rejeita com o erro dele, que volta
 * para o campo certo (ver falhaDaTroca).
 *
 * Fora do design: o checklist exige "Letras e números" (o backend recusa
 * senha sem dígito) — ver regrasDaSenha.
 */
import React, { useEffect, useRef, useState } from "react";

import { CampoDeSenha, MedidorDeForca, Requisitos } from "./CamposDaSenha";
import { MensagemErro, MensagemSucesso } from "./MensagemCampo";
import {
  CAMPO_CONFIRMACAO,
  CAMPO_DO_FORMULARIO_GERAL,
  CAMPO_NOVA,
  CAMPO_PROVISORIA,
  CONFERE,
  MENSAGEM_SENHAS_NAO_CONFEREM,
  NAO_CONFERE,
  conferenciaDaConfirmacao,
  erroDaTroca,
  falhaDaTroca,
} from "./regrasDaSenha";
import { IconeCheck, IconeChave, IconeVoltar } from "./icones";

const PARADO = "idle";
const SALVANDO = "busy";
const DEFINIDA = "ok";

const ID_REQUISITOS = "erp-pw-reqs";

function ConteudoDoBotao({ status }) {
  if (status === SALVANDO) {
    return (
      <>
        <span className="spin" aria-hidden="true" />
        Salvando…
      </>
    );
  }
  if (status === DEFINIDA) {
    return (
      <>
        <IconeCheck />
        Senha definida
      </>
    );
  }
  return (
    <>
      <IconeChave />
      Definir senha
    </>
  );
}

export default function PainelPrimeiroAcesso({ definirSenha, onVoltar }) {
  const [valores, setValores] = useState({ provisoria: "", nova: "", confirmacao: "" });
  const [erros, setErros] = useState({});
  const [status, setStatus] = useState(PARADO);
  const montado = useRef(true);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const ocupado = status !== PARADO;
  const { provisoria, nova, confirmacao } = valores;

  function mudar(campo) {
    return (valor) => {
      setValores((atual) => ({ ...atual, [campo]: valor }));
      setErros({});
    };
  }

  async function enviar(evento) {
    evento.preventDefault();
    if (ocupado) return;
    const erro = erroDaTroca(valores);
    if (erro) {
      setErros({ [erro.campo]: erro.mensagem });
      return;
    }
    setErros({});
    setStatus(SALVANDO);
    try {
      await definirSenha(valores);
      if (montado.current) setStatus(DEFINIDA);
    } catch (falha) {
      if (!montado.current) return;
      setStatus(PARADO);
      setErros(falhaDaTroca(falha));
    }
  }

  const conferencia = conferenciaDaConfirmacao(nova, confirmacao);
  const erroConfirmacao = erros[CAMPO_CONFIRMACAO] || (conferencia === NAO_CONFERE ? MENSAGEM_SENHAS_NAO_CONFEREM : "");
  const descricaoNova = [ID_REQUISITOS, erros[CAMPO_NOVA] && "erp-pw-new-erro"].filter(Boolean).join(" ");

  return (
    <div className="pane mfa pwd">
      <button className="back" type="button" onClick={onVoltar} disabled={ocupado}>
        <IconeVoltar />
        Voltar ao login
      </button>
      <div className="mfa-i">
        <IconeChave />
      </div>
      <div className="kicker">Primeiro acesso</div>
      <h1 className="a-h">Crie sua senha definitiva</h1>
      <p className="a-p">Troque a senha provisória que você recebeu por uma senha só sua.</p>

      <form onSubmit={enviar} noValidate>
        <CampoDeSenha
          id="erp-pw-cur"
          rotulo="Senha provisória"
          autoComplete="current-password"
          placeholder="A senha que você recebeu"
          nomeDoOlho="senha provisória"
          valor={provisoria}
          aoMudar={mudar("provisoria")}
          invalido={Boolean(erros[CAMPO_PROVISORIA])}
          descritoPor={erros[CAMPO_PROVISORIA] && "erp-pw-cur-erro"}
          somenteLeitura={ocupado}
        >
          {erros[CAMPO_PROVISORIA] && <MensagemErro id="erp-pw-cur-erro">{erros[CAMPO_PROVISORIA]}</MensagemErro>}
        </CampoDeSenha>

        <CampoDeSenha
          id="erp-pw-new"
          rotulo="Nova senha"
          autoComplete="new-password"
          placeholder="Mínimo de 8 caracteres"
          nomeDoOlho="nova senha"
          valor={nova}
          aoMudar={mudar("nova")}
          invalido={Boolean(erros[CAMPO_NOVA])}
          descritoPor={descricaoNova}
          somenteLeitura={ocupado}
        >
          <MedidorDeForca senha={nova} />
          <Requisitos id={ID_REQUISITOS} nova={nova} provisoria={provisoria} />
          {erros[CAMPO_NOVA] && <MensagemErro id="erp-pw-new-erro">{erros[CAMPO_NOVA]}</MensagemErro>}
        </CampoDeSenha>

        <CampoDeSenha
          id="erp-pw-conf"
          rotulo="Confirmar nova senha"
          autoComplete="new-password"
          placeholder="Repita a nova senha"
          nomeDoOlho="confirmação"
          valor={confirmacao}
          aoMudar={mudar("confirmacao")}
          invalido={Boolean(erros[CAMPO_CONFIRMACAO])}
          descritoPor={erroConfirmacao && "erp-pw-conf-erro"}
          somenteLeitura={ocupado}
        >
          {erroConfirmacao && <MensagemErro id="erp-pw-conf-erro">{erroConfirmacao}</MensagemErro>}
          {!erroConfirmacao && conferencia === CONFERE && <MensagemSucesso>As senhas conferem.</MensagemSucesso>}
        </CampoDeSenha>

        {erros[CAMPO_DO_FORMULARIO_GERAL] && (
          <MensagemErro className="srv">{erros[CAMPO_DO_FORMULARIO_GERAL]}</MensagemErro>
        )}
        <button
          className={`e-btn${status === DEFINIDA ? " ok" : ""}${status === SALVANDO ? " busy" : ""}`}
          type="submit"
          aria-busy={status === SALVANDO || undefined}
        >
          <ConteudoDoBotao status={status} />
        </button>
      </form>

      <p className="alt">
        Dica: uma frase curta com números e um símbolo é forte e fácil de lembrar. Não reutilize senhas de outros sistemas.
      </p>
    </div>
  );
}
